import {
  AccountIcon,
  type AppShellAccount,
  type AppShellAssistant,
  HelpIcon,
  type IconProps,
  LogoutIcon,
  SettingsTwoToneIcon,
} from '@all-manager/design-system-v2';
import { type ComponentType, createElement, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useGetDriveConfig from '../../features/google-drive/api/useGetDriveConfig';
import { useGetSpaceAISettings } from '../../features/inbox/api/useGetSpaceAISettings';
import { useOnboardingEligibility } from '../../features/onboarding/hooks/useOnboardingEligibility';
import { openOnboarding } from '../../features/onboarding/store/onboardingStore';
import { useGlobalSearchShortcut } from '../../features/search/hooks/useGlobalSearchShortcut';
import { ROLE_LABELS } from '../../features/spaces/constants/roles';
import { useAuth } from '../../hooks/useAuth';
import { useSpace } from '../../hooks/useSpace';
import { usePermissions } from '../../hooks/usePermissions';
import { isPlainLeftClick } from '../../utils/isPlainLeftClick';
import { isDestinationVisible, NAVIGATION_GROUPS, type NavigationDestination } from './navigationGroups';

type NavigationEntry = {
  label: string;
  path: string;
  Icon: ComponentType<IconProps>;
  selected: boolean;
};

const SPACES_PATH = '/spaces';
const MAX_INITIALS = 2;

/** "natacha.doiselet@…" -> "ND", "alice@…" -> "A". */
export function getInitials(email: string): string {
  const localPart = email.split('@')[0] ?? '';
  return localPart
    .split(/[._-]+/)
    .filter((part) => part.length > 0)
    .slice(0, MAX_INITIALS)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function useLayout() {
  const { user, signOut } = useAuth();
  const permissions = usePermissions();
  const { isAdmin } = permissions;
  const { currentSpace, memberships, isSuperAdmin } = useSpace();
  const { data: driveConfig, isLoading: isDriveLoading } = useGetDriveConfig();
  const { data: isAiEnabled = false } = useGetSpaceAISettings({ spaceId: currentSpace?.id });
  const search = useGlobalSearchShortcut();
  const { consultationRole } = useOnboardingEligibility();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const basePath = `/spaces/${currentSpace?.slug}`;
  const settingsPath = `${basePath}/settings`;
  const accountPath = `${basePath}/account`;

  function isHomeSelected() {
    return pathname === basePath || pathname === `${basePath}/`;
  }

  function isSectionSelected(path: string) {
    return pathname === path || pathname.startsWith(`${path}/`);
  }

  function toEntry(destination: NavigationDestination): NavigationEntry {
    const path = `${basePath}${destination.subPath}`;
    const selected = destination.subPath === '' ? isHomeSelected() : isSectionSelected(path);
    return { label: destination.label, path, Icon: destination.Icon, selected };
  }

  const groups = NAVIGATION_GROUPS.map((group) => ({
    title: group.title,
    entries: group.destinations.filter((destination) => isDestinationVisible(destination, permissions)).map(toEntry),
  }));

  // Kept apart from the work groups, at the bottom of the sidebar.
  const settingsEntry: NavigationEntry | null = isAdmin
    ? {
      label: "Paramètres de l'espace",
      path: `${settingsPath}/copropriete`,
      Icon: SettingsTwoToneIcon,
      selected: isSectionSelected(settingsPath),
    }
    : null;

  /** Router navigation without `stopPropagation`, so the shell can close its phone drawer. */
  function handleLinkClick(event: MouseEvent<HTMLElement>, path: string) {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    navigate(path);
  }

  function handleSwitchSpaceClick(event: MouseEvent<HTMLElement>) {
    handleLinkClick(event, SPACES_PATH);
  }

  function handleSignOut() {
    void signOut();
  }

  const assistantPath = `${basePath}/assistant`;
  const assistant: AppShellAssistant | undefined = isAiEnabled && permissions.canUseAssistant
    ? {
      label: "Demander à l'assistant",
      href: assistantPath,
      selected: isSectionSelected(assistantPath),
      onClick: (event) => handleLinkClick(event, assistantPath),
    }
    : undefined;

  // Only admins can connect Drive: the warning is theirs to act on.
  const driveAlert = isAdmin && !isDriveLoading && !driveConfig ? 'Google Drive non connecté' : undefined;
  const currentRole = memberships.find((membership) => membership.space.slug === currentSpace?.slug)?.role;
  const email = user?.email ?? '';

  const account: AppShellAccount = {
    label: email,
    initials: getInitials(email),
    detail: currentRole ? ROLE_LABELS[currentRole] : undefined,
    menuItems: [
      {
        id: 'account',
        label: 'Mon compte',
        icon: createElement(AccountIcon, { fontSize: 'small' }),
        href: accountPath,
        onClick: (event) => handleLinkClick(event, accountPath),
      },
      // Replays the first-run tour, only written for members who consult the space.
      ...(consultationRole
        ? [{
          id: 'onboarding',
          label: "Présentation de l'espace",
          icon: createElement(HelpIcon, { fontSize: 'small' }),
          onClick: openOnboarding,
        }]
        : []),
      {
        id: 'logout',
        label: 'Se déconnecter',
        icon: createElement(LogoutIcon, { fontSize: 'small' }),
        dividerBefore: true,
        onClick: handleSignOut,
      },
    ],
  };

  return {
    spaceName: currentSpace?.name ?? '',
    groups,
    settingsEntry,
    driveAlert,
    handleLinkClick,
    search,
    assistant,
    account,
    switchSpace: isSuperAdmin || memberships.length > 1
      ? { label: "Changer d'espace", href: SPACES_PATH, onClick: handleSwitchSpaceClick }
      : undefined,
  };
}
